import { decodeJwtWeb, IResponse, SESSION_USER_CLAIMS, spLogTags } from "@eventr/common";
import { AccessToken, AppleProviderResponse, SocialLogin } from "../../plugins/socialLogin/SocialLoginPlugin";

import { Capacitor } from "@capacitor/core";
import { useToken } from "@eventr/common-storybook";
import qs from "qs";
import queryString from "query-string";
import { routes } from "routing";
import { v1 as uuidv1 } from "uuid";
import { StateCreator } from "zustand";
import { ApiClient } from "../../data/apiClientCtor";
import { EventrGatewayAPI } from "../../data/gateway.api";
import { comboInterceptorResponse } from "../../data/interseptors/comboInterResp";
import { logInterReq } from "../../data/interseptors/logInterReq";
import { logInterReqError } from "../../data/interseptors/logInterReqError";
import { logInterResponseError } from "../../data/interseptors/logInterResponseError";
import i18n from "../../i18n/i18next";
import { entraLogin, EntraProviderResponse } from "../../plugins/socialLogin/browserOAuth";
import { BoundStore, bs, useBoundStore } from "../../state/bound.store";
import { useOverlayStore } from "../../state/overlayState/overlay.store";
import { defaultAddSnack } from "../../state/overlayState/overlayContext";
import { bytesToBase64, claimDerivedTabFlags, getRandomString } from "../../utils";
import { useRegStore } from "../authentication/registrationProvider";
import { logEvent } from "../firebase/firebase";
import { registerResetFn } from "../appState/resetState";
import { useGuestStore } from "../guest/guest.store";
import { isIosNativeNavigation } from "../nativeNavigation/platform";
import { oidcConfig } from "./config";
import { OAUTH_ENDPOINTS } from "./endpoints";
import { ICodeExchangeBody, IEmailSignInBody, ITokenResponse } from "./interfaces";

export function isNetworkErrorMessage(message?: string): boolean {
  return !!message && (message.includes("Network Error") || message.includes("timeout"));
}

/**
 * Whether a failed sign-in response means "no account with that email", as opposed to the
 * request having been rejected for some other reason.
 *
 * ApiClient reports every failure the same way — `message` is the axios line ("Request failed
 * with status code 400") and `data` is the whole response — so the status code is the only
 * thing that separates them. Keycloak answers 404 for an unknown user; a rejected
 * `redirect_uri`, which is what this origin missing from the `eventrApp` client's allowlist
 * looks like, is a 400. Reporting that as a wrong email address hides it completely.
 */
export function isUnknownUserResponse(resp: { data?: unknown }): boolean {
  const response = resp.data as { status?: unknown } | undefined;
  return response?.status === 404;
}

interface OauthState {
  IDtoken: string | undefined;
  refreshToken: string | undefined;
  expiration: number | undefined;
  loginCompleted: boolean;
}

interface GoogleLoginResponseOnline {
  accessToken: AccessToken | null;
  idToken: string | null;
  profile: {
    email: string | null;
    familyName: string | null;
    givenName: string | null;
    id: string | null;
    name: string | null;
    imageUrl: string | null;
  };
  responseType: "online";
}
interface OauthActions {
  refreshTokens: () => Promise<IResponse<ITokenResponse>>;

  googleOneTapSignIn: () => Promise<{
    googleResult: GoogleLoginResponseOnline;
    resp: IResponse<ITokenResponse>;
  }>;
  appleOneTapSignIn: () => Promise<{
    appleResult: AppleProviderResponse;
    resp: IResponse<ITokenResponse>;
  }>;
  entraOneTapSignIn: () => Promise<{
    entraResult: EntraProviderResponse;
    resp: IResponse<ITokenResponse>;
  }>;
  signInWithPassword: (
    email: string,
    password: string,
    redirectPath?: string | null,
  ) => Promise<IResponse<ITokenResponse>>;
  emailSignIn: (email: string) => Promise<IResponse<null>>;
  signInWithProvider: (provider: "google" | "apple" | "entra") => Promise<void>;
  setTokens: (tokens: any) => void;
  signInRedirect: () => void;
  exchangeCode: (code: string) => Promise<IResponse<ITokenResponse>>;
  commitTokens: (token: ITokenResponse) => void;
}

const getSocialConfig = () => {
  const google = {
    webClientId: "974218315221-uj2ae1qq95larem28jf1v1d4jto0s6mn.apps.googleusercontent.com",
    iOSClientId: "974218315221-l5dpb7hadqbbdu1cq7qfgsenuhj2fs1p.apps.googleusercontent.com",
    mode: "online",
    // iOSServerClientId
  };
  if (Capacitor.getPlatform() === "ios")
    return {
      google: google,
      // apple needs config only for android
      apple: {
        clientId: "com.softpauer.eventr.signin",
      },
      entra: {
        clientId: "bad81977-84cc-4d19-9492-3828d507b848",
        mode: "online",
      },
    };
  if (Capacitor.getPlatform() === "android")
    return {
      google: google,
    };
  return {
    google: google,
    apple: {
      clientId: "com.softpauer.eventr.signin",
      // Apple matches this exactly against the Services ID's Return URLs, so it can't default
      // to window.location.href, which varies with the route the sign-in button is on.
      redirectUrl: `${window.location.origin}/`,
    },
    entra: {
      clientId: "bad81977-84cc-4d19-9492-3828d507b848",
      mode: "online",
    },
  };
};

export interface OauthSlice extends OauthState, OauthActions {}
let gloabalRefreshTokenCall: Promise<IResponse<ITokenResponse>> | null = null;
export const createOauthSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], OauthSlice> = (set, get) => {
  SocialLogin.initialize(getSocialConfig() as any);
  return {
    IDtoken: undefined,
    refreshToken: undefined,
    expiration: undefined,
    loginCompleted: false,

    googleOneTapSignIn: async () => {
      const googleResp = await SocialLogin.login({
        provider: "google",

        options: { scopes: ["email", "profile", "openid"] },
      });

      const googleResult = googleResp.result as GoogleLoginResponseOnline;

      // lets see if the user already exist
      const bodyFields = {
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: oidcConfig().client_id,
        subject_token: googleResult?.accessToken?.token,
        subject_issuer: "google",
        client_secret: oidcConfig().client_secret,
        subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
      };
      const resp = await keycloakClient().post<any, ITokenResponse>(OAUTH_ENDPOINTS.token, qs.stringify(bodyFields), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return {
        googleResult,
        resp,
      };
    },

    appleOneTapSignIn: async () => {
      const appleResp = await SocialLogin.login({
        provider: "apple",
        options: { scopes: ["email", "name"] },
      });
      const appleResult = appleResp.result as AppleProviderResponse;
      // lets see if the user already exist
      const bodyFields = {
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: oidcConfig().client_id,
        subject_token: appleResult?.accessToken?.token,
        subject_issuer: "apple",
        client_secret: oidcConfig().client_secret,
        subject_token_type: "urn:ietf:params:oauth:token-type:id_token",
      };
      const resp = await keycloakClient().post<any, ITokenResponse>(OAUTH_ENDPOINTS.token, qs.stringify(bodyFields), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return { appleResult, resp };
    },

    entraOneTapSignIn: async () => {
      const entraResp = await entraLogin();
      const entraResult = entraResp.result;

      const subjectToken = entraResult.accessToken?.token;
      if (!subjectToken) {
        return {
          entraResult,
          resp: { status: "error", message: "Entra sign-in returned no access token" },
        };
      }

      // lets see if the user already exist
      const bodyFields = {
        grant_type: "urn:ietf:params:oauth:grant-type:token-exchange",
        client_id: oidcConfig().client_id,
        subject_token: subjectToken,
        subject_issuer: "microsoft",
        client_secret: oidcConfig().client_secret,
        subject_token_type: "urn:ietf:params:oauth:token-type:access_token",
      };
      const resp = await keycloakClient().post<any, ITokenResponse>(OAUTH_ENDPOINTS.token, qs.stringify(bodyFields), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return { entraResult, resp };
    },
    refreshTokens: async () => {
      const { refreshToken, expiration } = get();

      if (refreshToken && expiration) {
        if (Date.now() < expiration - 20000) {
          return { status: "error", message: "not expired" };
        }
        spLogTags("refreshing tokens", ["OAUTH"]);
        if (gloabalRefreshTokenCall !== null) {
          spLogTags("refreshing tokens already in progress returning existing promise", ["OAUTH"]);
          return gloabalRefreshTokenCall;
        }
        const refreshTokenCall = async (): Promise<IResponse<ITokenResponse>> => {
          const bodyFields = {
            grant_type: "refresh_token",
            client_id: oidcConfig().client_id,
            client_secret: oidcConfig().client_secret,
            refresh_token: refreshToken,
          };
          const resp = await keycloakClient().post<any, ITokenResponse>(OAUTH_ENDPOINTS.token, bodyFields, {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
            },
          });
          if (resp.status === "success") {
            const token = resp.data!;
            set(
              {
                IDtoken: token.access_token,
                refreshToken: token.refresh_token,
                expiration: Date.now() + token.expires_in * 1000,
              },
              false,
              "refreshTokens",
            );

            return { status: "success", data: token };
          } else {
            if (resp.data && (resp.data as any).data.error === "invalid_grant") {
              /// Request for new token failed as session not active
              spLogTags("refreshing tokens failed as session not active, logging user out", ["OAUTH"], resp);
              get().logout();
            }

            spLogTags("refreshing tokens failed", ["OAUTH"], resp);
            return { status: "error", message: "refresh failed" };
          }
        };
        gloabalRefreshTokenCall = refreshTokenCall();

        return gloabalRefreshTokenCall.finally(() => {
          gloabalRefreshTokenCall = null;
        });
      } else {
        return { status: "error", message: "no refresh token" };
      }
    },
    emailSignIn: async (email: string) => {
      logEvent("sign_in_email", {});
      const nonce = uuidv1();
      const codeVerifier = getRandomString(43);

      const body: IEmailSignInBody = {
        email: email.toLowerCase(),
        client_id: oidcConfig().client_id,
        redirect_uri: oidcConfig().redirect_uri,
        expiration_seconds: 60000,
        nonce: nonce,
        state: codeVerifier,
        remember_me: true,
        scope: "openid profile email offline_access",
      };
      const resp = await keycloakClient().post<IEmailSignInBody, null>(OAUTH_ENDPOINTS.sendEmailLogin, body, {
        timeout: 15000,
      });

      if (resp.status === "error") {
        spLogTags(resp.message, ["ERROR", "OAUTH"], resp);
        if (isNetworkErrorMessage(resp.message)) {
          defaultAddSnack(i18n.t("snack.connectionError"), "error");
        } else if (!isUnknownUserResponse(resp)) {
          // An unknown user is the one failure the form can speak to, and it shows a field
          // error instead. Everything else is a server or configuration fault — most often
          // this origin missing from the eventrApp client — and needs to say so rather than
          // blame the address the user typed.
          defaultAddSnack(i18n.t("snack.try-later"), "error");
        }
      } else {
        window.pushPath(`${routes.auth.signInEmailSent.path}?email=${encodeURIComponent(email)}`);
      }
      return resp;
    },
    signInWithPassword: async (email: string, password: string, redirectPath: string | null = routes.home.path) => {
      logEvent("sign_in_password", {});
      const str = `${oidcConfig().client_id}:${oidcConfig().client_secret}`;
      const base64Auth = bytesToBase64(new TextEncoder().encode(str));
      const headers = {
        Authorization: `Basic ${base64Auth}`,
        "Content-Type": "application/x-www-form-urlencoded",
      };
      const bodyFields = {
        grant_type: "password",
        audience: oidcConfig().client_id,
        username: email,
        password: password,
      };
      const resp = await keycloakClient().post<any, ITokenResponse>(OAUTH_ENDPOINTS.token, bodyFields, {
        headers: headers,
      });
      if (resp.status === "error") {
        spLogTags(resp.message, ["ERROR", "OAUTH"], resp);
        // Non-network failures show a field error on the sign-in form instead.
        if (isNetworkErrorMessage(resp.message)) {
          defaultAddSnack(i18n.t("snack.connectionError"), "error");
        }
      } else {
        const token = resp.data!;
        set({
          IDtoken: token.access_token,
          refreshToken: token.refresh_token,
          expiration: Date.now() + token.expires_in * 1000,
        });
        await get().onLogin();

        // Under iOS native navigation the root controller swaps to the tab root on
        // login, so a redundant push to home would duplicate the home view. An explicit
        // non-home redirect (e.g. a deeplink target) still navigates.
        if (redirectPath && !(isIosNativeNavigation() && redirectPath === routes.home.path)) {
          window.pushPath(redirectPath);
        }
      }

      return resp;
    },
    // this is a redirect to the keycloak login page sign in Dont use it! this is just to impersonate
    signInRedirect: async () => {
      const queryParams = queryString.stringify({
        client_id: oidcConfig().client_id,
        redirect_uri: oidcConfig().redirect_uri,
        response_type: "code",
        scope: "openid",
      });
      const url = `${oidcConfig().authority}/realms/master/protocol/openid-connect/auth?${queryParams}`;
      window.location.href = url;
    },

    // Leaves the session untouched either way. Call commitTokens with the result to sign in.
    exchangeCode: async (code: string): Promise<IResponse<ITokenResponse>> => {
      const bodyFields: ICodeExchangeBody = {
        grant_type: "authorization_code",
        client_id: oidcConfig().client_id,
        client_secret: oidcConfig().client_secret,
        redirect_uri: oidcConfig().redirect_uri,
        code: code,
      };
      return keycloakClient().post<ICodeExchangeBody, ITokenResponse>(OAUTH_ENDPOINTS.token, bodyFields, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
    },
    commitTokens: (token: ITokenResponse) => {
      set({
        IDtoken: token.access_token,
        refreshToken: token.refresh_token,
        expiration: Date.now() + token.expires_in * 1000,
      });
    },
    signInWithProvider: async (provider: "google" | "apple" | "entra") => {
      let tokenResp: IResponse<ITokenResponse>;
      let providerResult: {
        email?: string;
        firstName?: string;
        lastName?: string;
        phoneNumber?: string;
      };
      useOverlayStore.getState().setLoading(true);
      try {
        if (provider === "google") {
          const { googleResult, resp } = await bs().googleOneTapSignIn();

          tokenResp = resp;
          providerResult = {
            email: googleResult.profile.email ?? undefined,
            firstName: googleResult.profile.givenName ?? undefined,
            lastName: googleResult.profile.familyName ?? undefined,
          };
        } else if (provider === "apple") {
          const { appleResult, resp } = await bs().appleOneTapSignIn();
          tokenResp = resp;
          providerResult = {
            email: appleResult.profile.email ?? undefined,
            firstName: appleResult.profile.givenName ?? undefined,
            lastName: appleResult.profile.familyName ?? undefined,
          };
        } else {
          const { resp, entraResult } = await bs().entraOneTapSignIn();
          tokenResp = resp;
          providerResult = {
            email: entraResult.profile?.email,
            firstName: entraResult.profile?.givenName,
            lastName: entraResult.profile?.familyName,
            phoneNumber: entraResult.profile?.mobilePhone,
          };
        }

        if (tokenResp.status === "error" || !tokenResp.data) {
          spLogTags(tokenResp.message, ["ERROR", "OAUTH"], tokenResp);
          if (isNetworkErrorMessage(tokenResp.message)) {
            defaultAddSnack(i18n.t("snack.connectionError"), "error");
          }
          return;
        }
        const tokenData = tokenResp.data;

        const profile = await EventrGatewayAPI.profile.getMe({
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
          },
        });
        if (profile.status === "success") {
          const token = tokenData;
          set({
            IDtoken: token.access_token,
            refreshToken: token.refresh_token,
            expiration: Date.now() + token.expires_in * 1000,
          });

          window.replacePath(routes.home.path);
          await bs().onLogin();
        } else {
          // take to account creation flow
          const token = tokenData;
          useRegStore.getState().setInviteData({
            email: providerResult.email,
            firstName: providerResult.firstName,
            lastName: providerResult.lastName,
            phoneNumber: providerResult.phoneNumber,
            isEIDP: true,
            flow_type: "create",
            authTokens: {
              IdToken: token.access_token,
              refreshToken: token.refresh_token,
              expiration: Date.now() + token.expires_in * 1000,
            },
          });
          window.pushPath(routes.auth.signUpDetails.path);
        }
      } finally {
        useOverlayStore.getState().setLoading(false);
      }
    },
    setTokens: (tokens: any) => {
      set({
        IDtoken: tokens.IdToken,
        refreshToken: tokens.refreshToken,
        expiration: tokens.expiration,
      });
    },
  };
};

const keycloakClient = () => {
  const client = new ApiClient({
    baseURL: oidcConfig().authority,
    timeout: 20000,
  });

  client.setInterceptors(comboInterceptorResponse, logInterResponseError, logInterReq, logInterReqError);
  return client;
};

// The gateway mints a fresh claims JWT on every response, so the raw string differs even
// when the claims inside are identical. Hold the newest here so outgoing requests stay
// current, and compare decoded claims before writing: a write bumps claimsVersion, which
// refetches the itinerary, which returns another token.
let latestClaims: { userId: number; token: string } | undefined;
let lastClaimsSignature: string | undefined;

// Session state: without this it outlives logout, and the next sign-in dedupes against the
// previous session's claims.
registerResetFn(() => {
  latestClaims = undefined;
  lastClaimsSignature = undefined;
});

// Keyed by user and org too, so switching either counts as a change. Undefined when the
// token will not decode, so an unreadable one is never deduplicated away.
function claimsSignature(userId: number, token: string): string | undefined {
  try {
    const decoded = decodeJwtWeb(token);
    return JSON.stringify([userId, bs().selectedOrg?.name, decoded?.claims, decoded?.tier]);
  } catch {
    return undefined;
  }
}

export function getClaimsJWT() {
  const user = bs()?.wpUser;
  if (!user) return null;

  const claims = latestClaims?.userId === user.id ? latestClaims.token : user.claimsJWT;

  if (!claims || claims === "null") {
    return null;
  }
  return claims;
}

export function setClaimsJWT(token: string) {
  if (!token) return;
  const userData = bs().wpUser ?? null;

  if (!userData) {
    return;
  }

  if (userData.claimsJWT === token) return;
  latestClaims = { userId: userData.id, token };

  const signature = claimsSignature(userData.id, token);
  if (signature !== undefined && signature === lastClaimsSignature) return;
  lastClaimsSignature = signature;

  storeClaims(token);
  useBoundStore.setState({
    wpUser: {
      ...userData,
      claimsJWT: token,
    },
  });
}
/**
 * Drops the claims ahead of an organisation switch, whose claims are a different set.
 *
 * Clearing the dedupe is the point of this over a bare `localStorage.removeItem`. The
 * response-header path writes nothing when the decoded claims match the last ones it saw,
 * and that comparison outlived the removal: the organisation being switched to could hand
 * back a token identical to the one already seen for it, and the claims that had just been
 * deleted were then never written back for the rest of the session.
 *
 * `claimsVersion` is deliberately not bumped here. Subscribers to it refetch the selected
 * itinerary, which at this point is still the previous organisation's, against the new
 * organisation's base URL. The window this leaves is closed by whoever switched the
 * organisation refetching the claims, not by broadcasting an empty read.
 */
export const clearStoredClaims = () => {
  localStorage.removeItem(SESSION_USER_CLAIMS);
  latestClaims = undefined;
  lastClaimsSignature = undefined;
};

export const storeClaims = (token?: string) => {
  if (!token) return;
  // need to leave claims empty if in guest mode
  if (useGuestStore.getState().isGuestMode) return;
  const claimsDecode = decodeJwtWeb(token);
  const serialisedClaims = JSON.stringify(claimsDecode?.claims);
  // A re-issued token carrying the same claims leaves claimsVersion alone.
  const { userClaims, tier } = useBoundStore.getState();
  if (
    localStorage.getItem(SESSION_USER_CLAIMS) === serialisedClaims &&
    JSON.stringify(userClaims) === serialisedClaims &&
    tier === claimsDecode?.tier
  ) {
    return;
  }
  localStorage.setItem(SESSION_USER_CLAIMS, serialisedClaims);

  // Both tab flags re-derive from the localStorage claims written above, and both go in
  // this one write: the native tab bar reflows on either moving, and a reflow that sees
  // only half the pair installs a bar missing the other tab.
  useBoundStore.setState((state) => ({
    userClaims: claimsDecode?.claims,
    tier: claimsDecode?.tier,
    claimsVersion: state.claimsVersion + 1,
    ...claimDerivedTabFlags(),
  }));
};

const getToken = () => {
  return bs().IDtoken ?? "";
};
useToken.getState().setTokenAccessFunction(getToken);
